import {JsonPipe} from '@angular/common';
import {Component, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {MatTableModule} from '@angular/material/table';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {CmsApiService} from '../../../core/cms-api.service';
import {LoggerService} from '../../../core/logger.service';
import {OperationService} from '../../../operations/operation.service';

export interface MissingRequiredFieldRow {
  fieldTitle: string;
  indexName: string;
  count: number;
  subObjectType: string | null;
  superobjectTypeIds: string[];
  inputType?: string;
  fieldType?: string;
  unsupportedReason?: string;
  objects: any[];
}

export interface RequiredFieldValueSuggestion {
  value: string;
  display_value: string;
  count: number;
}

@Component({
  selector: 'app-admin-service-fill-required-fields',
  imports: [
    FormsModule,
    RouterLink,
    MatButton,
    MatFormField,
    MatLabel,
    MatIcon,
    MatInput,
    MatSelect,
    MatOption,
    MatRadioButton,
    MatRadioGroup,
    MatTableModule,
    MatProgressSpinner,
    JsonPipe,
  ],
  templateUrl: './admin-service-fill-required-fields.component.html',
  styleUrl: './admin-service-fill-required-fields.component.scss'
})
export class AdminServiceFillRequiredFieldsComponent {
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly operationService: OperationService = inject(OperationService);

  objectTypes = [
    {value: 'all', label: 'All types'},
    {value: 'object', label: 'Object'},
    {value: 'thing', label: 'Thing'},
    {value: 'artwork', label: 'Artwork'},
    {value: 'photograph', label: 'Photograph'},
    {value: 'building', label: 'Building'},
    {value: 'architecture', label: 'Architecture'},
    {value: 'design', label: 'Design'},
    {value: 'agent', label: 'Agent'},
    {value: 'place', label: 'Place'},
    {value: 'location', label: 'Location'},
    {value: 'collection', label: 'Collection'},
    {value: 'media', label: 'Media'},
    {value: 'image', label: 'Image'},
  ];

  selectedObjectType = 'all';
  scanning = false;
  scanError = '';
  missingFields: MissingRequiredFieldRow[] = [];
  displayedColumns = ['fieldTitle', 'indexName', 'count', 'subObjectType', 'support'];

  selectedField: MissingRequiredFieldRow | null = null;
  previewObjects: any[] = [];
  previewCount = 0;
  loadingField = false;

  suggestions: RequiredFieldValueSuggestion[] = [];
  selectedSuggestionValue = '';
  customValue = '';
  dryRunResult: any = null;
  applyResult: any = null;
  working = false;
  fieldError = '';

  get chosenValue(): string {
    if (this.selectedSuggestionValue === '__custom__') {
      return (this.customValue || '').trim();
    }
    return this.selectedSuggestionValue;
  }

  get canDryRun(): boolean {
    return !!this.selectedField && !this.selectedField.unsupportedReason && !!this.chosenValue && !this.working;
  }

  get canApply(): boolean {
    return this.canDryRun
      && this.dryRunResult
      && this.dryRunResult.index_field_name === this.selectedField?.indexName
      && String(this.dryRunResult.value) === this.chosenValue;
  }

  async scan(): Promise<void> {
    this.scanning = true;
    this.scanError = '';
    this.missingFields = [];
    this.clearFieldSelection();
    try {
      const res = await this.cms.findObjectsMissingRequiredFields(this.selectedObjectType);
      this.missingFields = this.mapMissingFields(res);
    } catch (e) {
      this.scanError = e?.errorMessage || e?.message || 'Scan failed';
      this.logger.error('Scan for missing required fields failed: ' + this.scanError);
    } finally {
      this.scanning = false;
    }
  }

  async selectField(row: MissingRequiredFieldRow): Promise<void> {
    this.selectedField = row;
    this.previewObjects = [];
    this.previewCount = row.count;
    this.suggestions = [];
    this.selectedSuggestionValue = '';
    this.customValue = '';
    this.dryRunResult = null;
    this.applyResult = null;
    this.fieldError = '';
    if (row.unsupportedReason) {
      return;
    }
    this.loadingField = true;
    try {
      const params = this.fieldQueryParams(row);
      const [preview, suggestionRes] = await Promise.all([
        this.cms.findObjectsMissingRequiredFieldForField(row.indexName, {
          ...params,
          start: 0,
          rows: 20
        }),
        this.cms.getRequiredFieldValueSuggestions(row.indexName, {...params, limit: 10})
      ]);
      const missing = preview?.missing?.[row.indexName] || {};
      this.previewObjects = missing.objects || [];
      this.previewCount = missing.count || row.count;
      this.suggestions = suggestionRes?.values || [];
      const suggested = suggestionRes?.suggested_value;
      if (suggested?.value) {
        this.selectedSuggestionValue = suggested.value;
      } else {
        this.selectedSuggestionValue = '__custom__';
      }
    } catch (e) {
      this.fieldError = e?.errorMessage || e?.message || 'Failed to load field details';
      this.logger.error(this.fieldError);
    } finally {
      this.loadingField = false;
    }
  }

  onValueChange(): void {
    this.dryRunResult = null;
    this.applyResult = null;
  }

  async dryRun(): Promise<void> {
    if (!this.canDryRun) {
      return;
    }
    this.working = true;
    this.fieldError = '';
    this.applyResult = null;
    try {
      this.dryRunResult = await this.cms.setMissingRequiredField(this.buildBody(true));
    } catch (e) {
      this.fieldError = e?.errorMessage || e?.message || 'Dry run failed';
    } finally {
      this.working = false;
    }
  }

  async apply(): Promise<void> {
    if (!this.canApply || !this.selectedField) {
      return;
    }
    const field = this.selectedField;
    const value = this.chosenValue;
    const count = this.dryRunResult?.missing_count ?? field.count;
    const confirmed = window.confirm(
      `Set "${field.fieldTitle}" (${field.indexName}) to "${this.displayValue(value)}" ` +
      `on ${count} object(s) missing this field?\n\n` +
      'The same value will be written on every object. Existing values will not be overwritten.'
    );
    if (!confirmed) {
      return;
    }
    this.working = true;
    this.fieldError = '';
    try {
      this.applyResult = await this.cms.setMissingRequiredField(this.buildBody(false));
      this.dryRunResult = null;
      if (this.applyResult?.queued) {
        this.operationService.notifyJobQueued();
      } else {
        await this.refreshSelectedField();
      }
    } catch (e) {
      this.fieldError = e?.errorMessage || e?.message || 'Apply failed';
    } finally {
      this.working = false;
    }
  }

  displayValue(value: string): string {
    const match = this.suggestions.find(item => item.value === value);
    return match?.display_value || value;
  }

  objectName(doc: any): string {
    return doc?.artifact_name || doc?.['artifact_name'] || '';
  }

  objectId(doc: any): string {
    return doc?.artifact_id || doc?.['artifact_id'] || '';
  }

  objectIdentifier(doc: any): string {
    return doc?.['identification_number.identification_number'] || '';
  }

  private mapMissingFields(res: any): MissingRequiredFieldRow[] {
    const missing = res?.missing || {};
    return Object.keys(missing).map(indexName => {
      const item = missing[indexName] || {};
      return {
        fieldTitle: item['Field title'] || indexName,
        indexName: item['Index name'] || indexName,
        count: item.count || 0,
        subObjectType: item['Sub object type'] || null,
        superobjectTypeIds: item['Superobject type ids'] || [],
        inputType: item.input_type,
        fieldType: item.field_type,
        unsupportedReason: item.unsupported_reason || undefined,
        objects: item.objects || []
      };
    }).sort((a, b) => b.count - a.count);
  }

  private fieldQueryParams(row: MissingRequiredFieldRow) {
    return {
      superobject_type_ids: (row.superobjectTypeIds || []).join(','),
      sub_object_type: row.subObjectType || undefined
    };
  }

  private buildBody(dryRun: boolean) {
    const field = this.selectedField;
    if (!field) {
      throw new Error('No field selected');
    }
    return {
      index_field_name: field.indexName,
      value: this.chosenValue,
      superobject_type_ids: field.superobjectTypeIds,
      sub_object_type: field.subObjectType || undefined,
      dry_run: dryRun,
      input_type: field.inputType,
      field_type: field.fieldType
    };
  }

  private async refreshSelectedField(): Promise<void> {
    if (!this.selectedField) {
      return;
    }
    const current = this.selectedField;
    const params = this.fieldQueryParams(current);
    const preview = await this.cms.findObjectsMissingRequiredFieldForField(current.indexName, {
      ...params,
      start: 0,
      rows: 20
    });
    const missing = preview?.missing?.[current.indexName] || {};
    current.count = missing.count || 0;
    this.previewObjects = missing.objects || [];
    this.previewCount = current.count;
    this.missingFields = this.missingFields
      .map(row => row.indexName === current.indexName ? {...current} : row)
      .filter(row => row.count > 0);
  }

  private clearFieldSelection(): void {
    this.selectedField = null;
    this.previewObjects = [];
    this.previewCount = 0;
    this.suggestions = [];
    this.selectedSuggestionValue = '';
    this.customValue = '';
    this.dryRunResult = null;
    this.applyResult = null;
    this.fieldError = '';
  }
}
